export type LeaderVeeraPanelHintValue =
  | string
  | number
  | boolean
  | null
  | readonly LeaderVeeraPanelHintValue[]
  | { readonly [key: string]: LeaderVeeraPanelHintValue };

export interface LeaderVeeraPanelLaunchData {
  readonly [key: string]: LeaderVeeraPanelHintValue | undefined;
}

export interface LeaderVeeraPanelLaunchContext {
  readonly pageName: string;
  readonly source: string;
  readonly data?: LeaderVeeraPanelLaunchData;
}

export interface LeaderVeeraPanelSnapshot {
  readonly open: boolean;
  readonly hasOpened: boolean;
  readonly context: LeaderVeeraPanelLaunchContext | null;
}

type LeaderVeeraPanelSubscriber = () => void;
type LeaderVeeraPanelInvoker = Pick<HTMLElement, "focus">;

const CLOSED_SNAPSHOT: LeaderVeeraPanelSnapshot = {
  open: false,
  hasOpened: false,
  context: null,
};

let snapshot = CLOSED_SNAPSHOT;
let launchFocusTarget: LeaderVeeraPanelInvoker | null = null;
let pendingFocusReturnTarget: LeaderVeeraPanelInvoker | null = null;
const subscribers = new Set<LeaderVeeraPanelSubscriber>();

const cloneHintValue = (
  value: LeaderVeeraPanelHintValue,
): LeaderVeeraPanelHintValue => {
  if (Array.isArray(value)) {
    return value.map(cloneHintValue);
  }
  if (value && typeof value === "object") {
    return Object.fromEntries(
      Object.entries(value).map(([key, nestedValue]) => [
        key,
        cloneHintValue(nestedValue),
      ]),
    );
  }
  return value;
};

const cloneLaunchData = (
  data: LeaderVeeraPanelLaunchData,
): LeaderVeeraPanelLaunchData => cloneHintValue(data) as LeaderVeeraPanelLaunchData;

const hintValuesEqual = (
  left: LeaderVeeraPanelHintValue | undefined,
  right: LeaderVeeraPanelHintValue | undefined,
): boolean => {
  if (left === right) return true;
  if (Array.isArray(left) || Array.isArray(right)) {
    return (
      Array.isArray(left) &&
      Array.isArray(right) &&
      left.length === right.length &&
      left.every((value, index) => hintValuesEqual(value, right[index]))
    );
  }
  if (left && right && typeof left === "object" && typeof right === "object") {
    const leftEntries = Object.entries(left);
    const rightKeys = Object.keys(right);
    return (
      leftEntries.length === rightKeys.length &&
      leftEntries.every(([key, value]) =>
        hintValuesEqual(value, right[key]),
      )
    );
  }
  return false;
};

const launchContextsEqual = (
  left: LeaderVeeraPanelLaunchContext | null,
  right: LeaderVeeraPanelLaunchContext,
): boolean =>
  left?.pageName === right.pageName &&
  left.source === right.source &&
  hintValuesEqual(left.data, right.data);

const publish = (nextSnapshot: LeaderVeeraPanelSnapshot) => {
  snapshot = nextSnapshot;
  subscribers.forEach((subscriber) => subscriber());
};

export const getLeaderVeeraPanelSnapshot = (): LeaderVeeraPanelSnapshot =>
  snapshot;

export const subscribeToLeaderVeeraPanel = (
  subscriber: LeaderVeeraPanelSubscriber,
): (() => void) => {
  subscribers.add(subscriber);
  return () => subscribers.delete(subscriber);
};

export const openLeaderVeeraPanel = (
  context: LeaderVeeraPanelLaunchContext,
  invoker?: LeaderVeeraPanelInvoker | null,
) => {
  const nextContext: LeaderVeeraPanelLaunchContext = {
    ...context,
    data: context.data ? cloneLaunchData(context.data) : undefined,
  };

  if (snapshot.open && launchContextsEqual(snapshot.context, nextContext)) {
    if (invoker !== undefined) {
      launchFocusTarget = invoker;
    }
    pendingFocusReturnTarget = null;
    return;
  }

  if (!snapshot.open || invoker !== undefined) {
    launchFocusTarget = invoker ?? null;
  }
  pendingFocusReturnTarget = null;
  publish({
    open: true,
    hasOpened: true,
    context: nextContext,
  });
};

export const closeLeaderVeeraPanel = () => {
  if (!snapshot.open) return;
  pendingFocusReturnTarget = launchFocusTarget;
  launchFocusTarget = null;
  publish({ ...snapshot, open: false });
};

export const restoreLeaderVeeraPanelFocus = () => {
  const target = pendingFocusReturnTarget;
  pendingFocusReturnTarget = null;
  target?.focus();
};

export const resetLeaderVeeraPanelBoundary = () => {
  launchFocusTarget = null;
  pendingFocusReturnTarget = null;
  publish(CLOSED_SNAPSHOT);
};

export const resetLeaderVeeraPanelStoreForTests = () => {
  snapshot = CLOSED_SNAPSHOT;
  launchFocusTarget = null;
  pendingFocusReturnTarget = null;
  subscribers.clear();
};
